import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { SignIn } from "@phosphor-icons/react";
import { toast } from "../components/ui/sonner";
import { loginUser, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await loginUser({ email, password });
      await refresh();
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="mx-auto flex min-h-[80vh] max-w-sm flex-col justify-center px-4 py-16">
      <div className="mb-8 text-center">
        <Link to="/" className="font-heading text-2xl font-black uppercase tracking-tighter text-white">
          EDGE<span className="text-eb-accent">BALL</span>
        </Link>
        <h1 className="mt-4 font-heading text-xl uppercase tracking-tight text-white">Log In</h1>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded-lg border border-white/10 bg-eb-surface p-6">
        {error && (
          <p data-testid="login-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
            {error}
          </p>
        )}
        <input
          data-testid="login-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="rounded-md border border-white/10 bg-eb-bg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-eb-accent focus:outline-none"
        />
        <input
          data-testid="login-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="rounded-md border border-white/10 bg-eb-bg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-eb-accent focus:outline-none"
        />
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={submitting}
          className="mt-2 flex items-center justify-center gap-2 rounded-md bg-eb-accent px-4 py-2 font-heading text-sm uppercase tracking-wide text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
        >
          <SignIn size={16} weight="bold" /> {submitting ? "Logging in\u2026" : "Log In"}
        </button>
      </form>
      <p className="mt-4 text-center text-sm text-zinc-500">
        Don&rsquo;t have an account?{" "}
        <Link to="/signup" data-testid="login-signup-link" className="text-eb-accent hover:underline">
          Sign up
        </Link>
      </p>
      <p className="mt-2 text-center text-sm text-zinc-500">
        <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-eb-accent hover:underline">
          Forgot password?
        </Link>
      </p>
    </div>
  );
};

export default Login;
